<template>
  <div v-if="isVisible" class="mini-voice-memo-player">
    <div class="mini-player-header">
      <div class="mini-player-title">{{ memoTitle }}</div>
      <button @click="close" class="mini-close-btn" title="Close">
        <i class="fas fa-times"></i>
      </button>
    </div>
    <div class="mini-player-controls">
      <button @click="rewind" class="mini-control-btn" title="Rewind 5s">
        <i class="fas fa-backward"></i>
      </button>
      <button
        @click="togglePlay"
        class="mini-control-btn mini-play-btn"
        :title="isPlaying ? 'Pause' : 'Play'"
      >
        <i :class="isPlaying ? 'fas fa-pause' : 'fas fa-play'"></i>
      </button>
      <div class="mini-time-display">
        {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
      </div>
    </div>
    <div class="mini-progress-container">
      <input
        type="range"
        class="mini-progress-bar"
        v-model="currentTime"
        :max="duration"
        @change="seek"
        @input="seek"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import axios from '@/axiosConfig';
import { useRoute } from 'vue-router';

const route = useRoute();
const isVisible = ref(false);
const memoId = ref(null);
const memoTitle = ref('Voice Memo');
const audioElement = ref(new Audio());
const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);

const togglePlay = () => {
  if (audioElement.value.paused) {
    audioElement.value.play();
    isPlaying.value = true;
  } else {
    audioElement.value.pause();
    isPlaying.value = false;
  }
};

const rewind = () => {
  audioElement.value.currentTime = Math.max(
    0,
    audioElement.value.currentTime - 5,
  );
};

const seek = () => {
  audioElement.value.currentTime = currentTime.value;
};

const close = () => {
  audioElement.value.pause();
  isPlaying.value = false;
  isVisible.value = false;
  memoId.value = null;
};

const updateProgress = () => {
  currentTime.value = audioElement.value.currentTime;
  duration.value = audioElement.value.duration || 0;
};

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const loadMemo = async (id) => {
  try {
    // First get memo details
    const response = await axios.get('/api/get-voice-memos');
    const memo = response.data.find((m) => m.id === parseInt(id));

    if (memo) {
      memoTitle.value = memo.title || 'Voice Memo';

      // Then get audio blob
      const blobResponse = await axios.get(`/api/get-audioblob/${id}`, {
        responseType: 'blob',
      });

      audioElement.value.src = URL.createObjectURL(blobResponse.data);
      audioElement.value.addEventListener('timeupdate', updateProgress);
      audioElement.value.addEventListener('ended', () => {
        isPlaying.value = false;
      });

      // Show player (don't auto-play to avoid browser restrictions)
      isVisible.value = true;

      // Try to auto-play, but don't fail if browser blocks it
      audioElement.value.play().catch((error) => {
        console.log(
          'Autoplay blocked by browser, user must click play:',
          error,
        );
        isPlaying.value = false;
      });
    }
  } catch (error) {
    console.error('Failed to load voice memo:', error);
    close();
  }
};

// Expose method to open the player
const open = (id) => {
  memoId.value = id;
  loadMemo(id);
};

// Listen for route changes to auto-open from URL
onMounted(() => {
  // Check URL params for shared memo on current page
  const checkUrlParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sharedMemoId = urlParams.get('voicememo');
    if (sharedMemoId) {
      open(sharedMemoId);
      return true;
    }
    return false;
  };

  // First check current URL
  if (!checkUrlParams()) {
    // If not in URL, check sessionStorage (in case it was stored during redirect)
    const storedMemoId = sessionStorage.getItem('pendingVoiceMemo');
    if (storedMemoId) {
      sessionStorage.removeItem('pendingVoiceMemo');
      open(storedMemoId);
    }
  }

  // Listen for clicks on voice memo links
  const handleClick = (e) => {
    const target = e.target.closest('a');
    if (target && target.href) {
      const url = new URL(target.href);
      const voicememoId = url.searchParams.get('voicememo');
      if (voicememoId) {
        e.preventDefault();
        open(voicememoId);
      }
    }
  };
  document.addEventListener('click', handleClick, true);

  // Cleanup
  const cleanup = () => {
    document.removeEventListener('click', handleClick, true);
  };

  onBeforeUnmount(cleanup);
});

// Watch for route query parameter changes
watch(
  () => route.query.voicememo,
  (newMemoId) => {
    if (newMemoId) {
      open(newMemoId);
    }
  },
);

onBeforeUnmount(() => {
  if (audioElement.value) {
    audioElement.value.pause();
    audioElement.value.removeEventListener('timeupdate', updateProgress);
  }
});

// Expose open method for parent components
defineExpose({ open });
</script>

<style scoped>
.mini-voice-memo-player {
  position: fixed;
  bottom: 10px;
  left: 10px;
  width: 400px;
  max-width: calc(92vw - 20px);
  background: white;
  border: 2px solid #a080a8;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 10000;
  padding: 12px;
  font-family: Arial, sans-serif;
}

.mini-player-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.mini-player-title {
  font-weight: bold;
  font-size: 14px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  margin-right: 10px;
}

.mini-close-btn {
  background: transparent;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mini-close-btn:hover {
  color: #000;
}

.mini-player-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.mini-control-btn {
  background: #a080a8;
  color: white;
  border: none;
  border-radius: 4px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.mini-control-btn:hover {
  background: #8a6a8e;
}

.mini-control-btn:active {
  transform: scale(0.95);
}

.mini-play-btn {
  width: 40px;
  height: 40px;
  font-size: 16px;
}

.mini-time-display {
  font-size: 12px;
  color: #666;
  font-family: monospace;
  margin-left: auto;
}

.mini-progress-container {
  width: 100%;
}

.mini-progress-bar {
  width: 100%;
  height: 6px;
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  cursor: pointer;
}

.mini-progress-bar::-webkit-slider-runnable-track {
  height: 6px;
  background: #ddd;
  border-radius: 3px;
}

.mini-progress-bar::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  height: 14px;
  width: 14px;
  background: #a080a8;
  border-radius: 50%;
  cursor: pointer;
  margin-top: -4px;
}

.mini-progress-bar::-moz-range-track {
  height: 6px;
  background: #ddd;
  border-radius: 3px;
}

.mini-progress-bar::-moz-range-thumb {
  height: 14px;
  width: 14px;
  background: #a080a8;
  border-radius: 50%;
  cursor: pointer;
  border: none;
}

@media (max-width: 480px) {
  .mini-voice-memo-player {
    width: calc(100vw - 20px);
    left: 10px;
    right: 10px;
  }
}
</style>
